import React from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import { MdAdd, MdAttachFile, MdCalculate, MdDeleteOutline } from 'react-icons/md';
import { WEIGHT_INPUT_STEP } from '../components/helpers';
import FilterableStatusSelect from './FilterableStatusSelect';
import { monthIndexForDate, monthLabelFor } from './csdForecastFormHelpers';

// One building × month row of "Building Monthly Weights (MT)". The row's month
// is derived from its Expected Date of Release — CSD picks the date, the Month
// column follows (29-Sep-2026 → Sep-2026).

const weightInputClass =
  'tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded-sm tw-text-xs tw-text-right disabled:tw-bg-gray-100 focus:tw-outline-none focus:tw-border-blue-400';
const actionButtonClass = 'tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-sm tw-border tw-transition-colors';
const disabledButtonClass = 'tw-border-gray-200 tw-bg-gray-50 tw-text-gray-300 tw-cursor-not-allowed';

const WEIGHT_FIELDS = ['built_up_weight', 'cold_formed_weight', 'sheeting_weight'];

const SourceDocument = ({ document }) => {
  if (!document) return <span className="tw-text-slate-300 tw-text-xs">—</span>;

  const content = (
    <>
      <MdAttachFile size={12} className="tw-shrink-0" />
      <span className="tw-truncate tw-text-xs">{document.filename}</span>
    </>
  );

  return document.url ? (
    <a
      href={document.url}
      target="_blank"
      rel="noreferrer"
      title={document.filename}
      className="tw-inline-flex tw-items-center tw-gap-1 tw-max-w-[110px] tw-text-blue-600 hover:tw-text-blue-800 hover:tw-underline tw-transition-colors"
    >
      {content}
    </a>
  ) : (
    <span title={document.filename} className="tw-inline-flex tw-items-center tw-gap-1 tw-max-w-[110px] tw-text-slate-500">
      {content}
    </span>
  );
};
SourceDocument.propTypes = { document: PropTypes.shape({ url: PropTypes.string, filename: PropTypes.string }) };

const CsdMonthlyRow = ({
  index, field, row, period, forecastWindow, control, setValue, getValues,
  buildingStatuses, isLocked, isFirstRow, canSplit, canDelete, canRecalculate, calculating,
  onSplit, onCalculate, onDelete,
}) => {
  const monthLabel = monthLabelFor(period, row?.month_index);

  // The date must sit in the forecast window and not reuse a month another row
  // of the same building already has (one row per building per month).
  const validateDate = (value) => {
    if (!value) return 'Expected Date of Release is required';
    const monthIndex = monthIndexForDate(period, value);
    if (!monthIndex) return 'Date must be within the forecast months';
    const clash = (getValues('monthlies') || [])
      .some((m, i) => i !== index && m.building_id === field.building_id && m.month_index === monthIndex);
    return clash ? `${field.building_name} already has a row in ${monthLabelFor(period, monthIndex)}` : true;
  };

  return (
    <tr className={`tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 tw-align-top ${!isFirstRow ? 'tw-bg-blue-50/30' : ''}`}>
      <td className="tw-px-3 tw-py-2 tw-border-r tw-border-gray-200 tw-font-medium tw-whitespace-nowrap">
        {isFirstRow
          ? field.building_name
          : <span className="tw-text-blue-400 tw-text-xs tw-pl-2">↳ {field.building_name}</span>}
      </td>

      <td className="tw-px-1.5 tw-py-1.5 tw-border-r tw-border-gray-200">
        <Controller
          control={control}
          name={`monthlies.${index}.phase`}
          render={({ field: f }) => (
            <input
              {...f}
              type="text"
              placeholder="e.g. P01"
              disabled={isLocked}
              className="tw-w-full tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded-sm tw-text-xs disabled:tw-bg-gray-100 focus:tw-outline-none focus:tw-border-blue-400"
            />
          )}
        />
      </td>

      {/* Expected Date of Release — drives the Month column */}
      <td className="tw-px-1.5 tw-py-1.5">
        <Controller
          control={control}
          name={`monthlies.${index}.expected_release_date`}
          rules={{ validate: validateDate }}
          render={({ field: f, fieldState: { error } }) => (
            <>
              <DatePicker
                selected={f.value ? dayjs(f.value).toDate() : null}
                onChange={(date) => {
                  const value = date ? dayjs(date).format('YYYY-MM-DD') : '';
                  f.onChange(value);
                  setValue(`monthlies.${index}.month_index`, monthIndexForDate(period, value), { shouldDirty: true });
                }}
                onBlur={f.onBlur}
                minDate={forecastWindow.minDate}
                maxDate={forecastWindow.maxDate}
                // A legacy row without a date opens on the month it is stored under.
                openToDate={!f.value && monthLabel ? dayjs(monthLabel, 'MMM-YYYY').toDate() : undefined}
                dateFormat="dd-MMM-yyyy"
                placeholderText="dd-MMM-yyyy"
                disabled={isLocked}
                ariaInvalid={error ? 'true' : undefined}
                className={`tw-w-full tw-px-2 tw-py-1 tw-border tw-rounded-sm tw-text-xs disabled:tw-bg-gray-100 focus:tw-outline-none focus:tw-border-blue-400 ${
                  error ? 'tw-border-red-500' : 'tw-border-gray-300'
                }`}
              />
              {error && <p className="tw-text-red-600 tw-text-[10px] tw-leading-tight tw-mt-1 tw-mb-0">{error.message}</p>}
            </>
          )}
        />
      </td>

      {/* Month — read-only, follows the date */}
      <td className="tw-px-3 tw-py-2 tw-border-r tw-border-gray-200 tw-text-center tw-whitespace-nowrap">
        {monthLabel
          ? <span className="tw-font-semibold tw-text-blue-700">{monthLabel}</span>
          : <span className="tw-text-gray-400">—</span>}
      </td>

      {WEIGHT_FIELDS.map((name, i) => (
        <td key={name} className={`tw-px-1 tw-py-1 ${i === WEIGHT_FIELDS.length - 1 ? 'tw-border-r tw-border-gray-200' : ''}`}>
          <Controller
            control={control}
            name={`monthlies.${index}.${name}`}
            render={({ field: f }) => (
              <input {...f} type="number" min={0} step={WEIGHT_INPUT_STEP} placeholder="0" disabled={isLocked} className={weightInputClass} />
            )}
          />
        </td>
      ))}

      <td className="tw-px-1.5 tw-py-1.5">
        <Controller
          control={control}
          name={`monthlies.${index}.project_status_name`}
          render={({ field: f }) => (
            <FilterableStatusSelect value={f.value || ''} options={buildingStatuses} onChange={f.onChange} disabled={isLocked} />
          )}
        />
      </td>

      <td className="tw-px-2 tw-py-1.5 tw-text-center">
        <SourceDocument document={row?.source_document} />
      </td>

      <td className="tw-px-2 tw-py-2 tw-text-center">
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
          <button
            type="button"
            onClick={() => onSplit(index)}
            disabled={!canSplit}
            title="Split: add this building for another month"
            className={`${actionButtonClass} ${canSplit
              ? 'tw-border-blue-200 tw-bg-white tw-text-blue-600 hover:tw-bg-blue-50 hover:tw-border-blue-400 tw-cursor-pointer'
              : disabledButtonClass}`}
          >
            <MdAdd size={14} />
          </button>
          {!isLocked && field.building_id && (
            <button
              type="button"
              onClick={() => onCalculate(index)}
              disabled={!canRecalculate || calculating}
              title={canRecalculate
                ? 'Recalculate weights from source document'
                : 'Save first — this row is new or its month changed'}
              className={`${actionButtonClass} ${canRecalculate && !calculating
                ? 'tw-border-yellow-300 tw-bg-white tw-text-yellow-600 hover:tw-bg-yellow-50 hover:tw-border-yellow-400 tw-cursor-pointer'
                : disabledButtonClass}`}
            >
              <MdCalculate size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(index)}
            disabled={!canDelete}
            title={canDelete ? 'Remove this month entry' : 'Cannot remove the only month for this building'}
            className={`${actionButtonClass} ${canDelete
              ? 'tw-border-red-200 tw-bg-white tw-text-red-600 hover:tw-bg-red-50 hover:tw-border-red-300 tw-cursor-pointer'
              : disabledButtonClass}`}
          >
            <MdDeleteOutline size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

CsdMonthlyRow.propTypes = {
  index: PropTypes.number.isRequired,
  field: PropTypes.object.isRequired,
  row: PropTypes.object,
  period: PropTypes.string,
  forecastWindow: PropTypes.shape({ minDate: PropTypes.instanceOf(Date), maxDate: PropTypes.instanceOf(Date) }).isRequired,
  control: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  buildingStatuses: PropTypes.array.isRequired,
  isLocked: PropTypes.bool,
  isFirstRow: PropTypes.bool,
  canSplit: PropTypes.bool,
  canDelete: PropTypes.bool,
  canRecalculate: PropTypes.bool,
  calculating: PropTypes.bool,
  onSplit: PropTypes.func.isRequired,
  onCalculate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CsdMonthlyRow;
